import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
} from "@material-tailwind/react";
import { CustomPhoneInput } from '../components/formInputs/CustomPhoneInput';
import { TooltipWithHelperIcon } from '../components/infoTooltip';
import CustomFileUpload from '../components/formInputs/CustomFileUpload';
import { Message } from '../components/message';
import { CustomRoadPartsSelector } from '../components/formInputs/CustomRoadPartsSelector';
import { CustomTrainingsSelector } from '../components/formInputs/CustomTrainingsSelector';
import { CustomAddressInput } from '../components/formInputs/CustomAddressInput';
import { initialFormData } from '../data/initialFormData';
import mailService from '../services/mailService';
import CustomTextInput from '../components/formInputs/CustomTextInput';
import CustomTextarea from '../components/formInputs/CustomTextArea';
import CustomEmailInput from '../components/formInputs/CustomEmailInput';
import CustomDateRangePicker from '../components/formInputs/CustomDateRangePicker';
import CustomSelect from '../components/formInputs/CustomSelect';
import { useTranslation } from 'react-i18next';
import { getTypeOfInterventions, getTypesOfUser } from '../data/contactData';
import CustomYesNoSwitch from '../components/formInputs/CustomYesNoSwitch';
import { signup_url_v2 } from '../data/navigationData';
import { useTheme } from '../DarkThemeContext';
import CustomMultiEmailInput from '../components/formInputs/CustomMultiEmailInput';
import CustomCheckBox from '../components/formInputs/CustomCheckBox';
import CustomMandatorySwitch from '../components/formInputs/CustomMandatorySwitch';

export default function ContactForm({ formType }) {

  const mail_cdco = process.env.REACT_APP_MAIL_CDCO;
  const mail_guichet = process.env.REACT_APP_MAIL_GUICHET;
  const mail_training = process.env.REACT_APP_MAIL_TRAINING;

  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Initial state setup
  const [formData, setFormData] = useState(initialFormData);
  const [showCreateAccountSuggestion, setShowCreateAccountSuggestion] = useState(false);

  // State for the overall validity of the form
  const [isFormValid, setIsFormValid] = useState(false);

  // State to hold the validity of individual fields
  const [fieldValidity, setFieldValidity] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    address: false,
    roadParts: false,
    B1: false,
    interventionType: false,
    userType: false,
    length: false,
    versionUsed: false,
    trainings: false,
    companyName: false,
    acceptTerms: false,
    newOrgaType: false,
    newOrgaBCE: false,
    newOrgaName: false,
    newOrgaPhone: false,
    newOrgaAddress: false,
    newOrgaLanguage: false
  });

  const areFieldsValidForFormType = useCallback(() => {
    switch (formType) {
      case 'bug':
        return (
          fieldValidity.firstName &&
          fieldValidity.lastName &&
          fieldValidity.email &&
          fieldValidity.message
        );
      case 'trainingRequest':
        return (
          fieldValidity.firstName &&
          fieldValidity.lastName &&
          fieldValidity.email &&
          fieldValidity.message &&
          fieldValidity.userType &&
          fieldValidity.trainings &&
          fieldValidity.acceptTerms
        );
      case 'assistance':
        return (
          fieldValidity.firstName &&
          fieldValidity.lastName &&
          fieldValidity.email &&
          fieldValidity.phone &&
          fieldValidity.interventionType &&
          fieldValidity.message
        );
      case 'apiAccess':
        return (
          fieldValidity.firstName &&
          fieldValidity.lastName &&
          fieldValidity.email &&
          fieldValidity.companyName &&
          fieldValidity.message &&
          fieldValidity.acceptTerms
        );
      case 'newOrganizationRequest':
        return (
          fieldValidity.firstName &&
          fieldValidity.lastName &&
          fieldValidity.email &&
          fieldValidity.phone &&
          fieldValidity.newOrgaType &&
          fieldValidity.newOrgaName &&
          fieldValidity.newOrgaBCE &&
          fieldValidity.newOrgaPhone &&
          fieldValidity.newOrgaAddress &&
          fieldValidity.newOrgaLanguage && 
          fieldValidity.message 
        );
      default:
        return (
          fieldValidity.firstName &&
          fieldValidity.lastName &&
          fieldValidity.email

        ); // Default form, checks for firstName, lastName, and email
    }
  }, [fieldValidity, formType]);


  // Handle changes in field validity
  const handleValidityChange = (fieldName, isValid) => {
    setFieldValidity(prev => ({ ...prev, [fieldName]: isValid }));
  };

  // Check the overall form validity whenever field validity changes
  useEffect(() => {
    setIsFormValid(areFieldsValidForFormType());
  }, [fieldValidity, areFieldsValidForFormType]);



  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [messageType, setMessageType] = useState('');
  const [message, setMessage] = useState('');

  const answers = [
    { label: t('yes'), value: "oui" },
    { label: t('no'), value: "non" },
  ];

  const versionUsed = [
    { label: t('cf-versionUsed-v2'), value: "V2" },
    { label: t('cf-versionUsed-v1'), value: "V1" },
  ];

  const orgaLanguages = [
    { label: t('cf-new-organization-form-organization-language-fr'), value: "Français" },
    { label: t('cf-new-organization-form-organization-language-nl'), value: "Néérlandais" },
    { label: t('cf-new-organization-form-organization-language-bilingual'), value: "Bilingue" },
  ];

  //Used to be sent in the email for CDCO (always in french)
  const ORGANIZATION_TYPES = {
    NO_BCE_ORGANIZATION: "Organisation sans BCE",
    EXPERT_OFFICE: "Bureau d'experts mandatés",
    INSTITUTIONAL_APPLICANT: "Impétrant institutionnel",
    EVENT_MANAGER: "Gestionnaire d'événements"
  };

  const orgaTypes = [
    { label: t('cf-new-organization-form-organization-type-no-bce'), value: ORGANIZATION_TYPES.NO_BCE_ORGANIZATION },
    { label: t('cf-new-organization-form-organization-type-event'), value: ORGANIZATION_TYPES.EVENT_MANAGER },
    { label: t('cf-new-organization-form-organization-type-expert'), value: ORGANIZATION_TYPES.EXPERT_OFFICE },
    { label: t('cf-new-organization-form-organization-type-institutional'), value: ORGANIZATION_TYPES.INSTITUTIONAL_APPLICANT },
  ];



  // Generic input change handler
  const handleInputChange = (field, value) => {

    setFormData(prevData => ({ ...prevData, [field]: value }));
  };

  // Submit handler
  const handleSubmit = async (event) => {

    event.preventDefault();
    
        const email = formData.email;
        const name = `${formData.firstName} ${formData.lastName}`;
        let subject = "";
        let body = "";
        let recipient = mail_guichet;
        const files = formData.files;

        switch (formType) {
          case "bug":
            subject = "Bug report";
            body += `
              Email : ${formData.email}

              Langue : ${currentLanguage}

              Version d'osiris: ${formData.versionUsed}

              Description du bug: 
              ${formData.message}`;
            recipient = mail_cdco;
            break;
          case "trainingRequest":
            subject = "Demande de formations";
            body += `
            Email : ${formData.email}

            Autres participants:  ${formData.emails.join('; ')}

            Type d'utilisateur : ${formData.userType}

            Langue : ${currentLanguage}

            Modules: ${formData.trainings}

            Message: 
            ${formData.message}
        `;
            recipient = mail_training;
            break;
          case "assistance":
            subject = "Demande d'assistance";
            body += `

              Email : ${formData.email}

              Numéro de téléphone : ${formData.countryCode} ${formData.phone}

              Type d'intervention : ${formData.interventionType}

              Numéro B1 : ${formData.B1}

              Message : 
              ${formData.message}`;
            break;
          case "apiAccess":
            subject = "Demande d'accès à l'API";
            body += `
              Email : ${formData.email}

              Nom de l'entreprise: ${formData.companyName}

              Message: ${formData.message}`;

            recipient = mail_cdco; 
            break;
          case "newOrganizationRequest":
            subject = "Demande de création d'une nouvelle organisation";
            body += `
              Prénom : ${formData.firstName}
              
              Nom de famille : ${formData.lastName}
              
              Adresse email : ${formData.email}

              Numéro de téléphone: ${formData.countryCode} ${formData.phone}

              -----------------------
              
              Type de l'organisation : ${formData.newOrgaType}
              
              Nom de l'organisation : ${formData.newOrgaName}

              N° BCE : ${formData.newOrgaBCE}

              Numéro de téléphone de l'organisation: ${formData.newOrgaCountryCode} ${formData.newOrgaPhone}              
              
              Adresse de l'organisation : ${formData.newOrgaAddress}

              Langue préférée : ${formData.newOrgaLanguage}
  
              Message: 
              ${formData.message}`;
              
              recipient = mail_cdco; 
            break;
            default:
              subject = "Contact Us";
            break;
        }

        if (isFormValid) {

          console.log(email, name, recipient, subject, body, files);

          setHasSubmitted(true);
          try {
            await mailService(email, name, recipient, subject, body, files);
            switch (formType) {
              case "newOrganizationRequest":
                setMessage(t('cf-new-organization-form-successMessage'));
              break;
              default:
                setMessage(t('cf-successMessage'));
            }
            setMessageType('success');

          } catch (error) {
            setMessage(t('cf-errorMessage'));
            setMessageType('error');
          }
        }
  };

  // Render conditionally based on formType
  const renderConditionalFields = () => {
    switch (formType) {
      case 'bug':
        return (
          <>
            <div className="text-blue-gray-600 dark:text-white">{t('cf-versionUsed')}</div>
            <CustomYesNoSwitch value={formData.versionUsed} onChange={(value) => handleInputChange('versionUsed', value)} options={versionUsed} setValidity={handleValidityChange} />
            <CustomTextarea
              label={t('cf-reportABug')}
              name="message"
              value={formData.message}
              onChange={(value) => handleInputChange('message', value)}
              setValidity={handleValidityChange}
            />
            <CustomFileUpload text={t('upload-file')} files={formData.files} onChange={(value) => handleInputChange('files', value)} />
          </>
        )
      case 'trainingRequest':
        return (
          <>
            <CustomSelect
              label={t('cf-userType')}
              name="userType"
              value={formData.userType}
              onChange={(value) => handleInputChange('userType', value)}
              setValidity={handleValidityChange}
              options={getTypesOfUser(t)}
            />
            <CustomMultiEmailInput onEmailChange={(emails) => handleInputChange('emails', emails)} label={t('cf-mail')} title={t('cf-add-mail-title')} />
            <CustomTrainingsSelector trainings={formData.trainings} onSelect={(value) => handleInputChange('trainings', value)} setValidity={handleValidityChange} />

            <CustomTextarea
              label={t('cf-needs')}
              name="message"
              value={formData.message}
              onChange={(value) => handleInputChange('message', value)}
              setValidity={handleValidityChange}
            />
            <CustomCheckBox name='acceptTerms' labelBeforeCliquableLink={t('cf-notice-training-label-before-link')} labelCliquableLink={t('cf-notice-training-label-link')} 
            routeCliquableLink={t('cf-notice-training-route-link')} labelAfterCliquableLink={t('cf-notice-training-label-after-link')} setValidity={handleValidityChange} />
          </>);
      case 'assistance':
        return (
          <>
            <CustomPhoneInput
              label={t('cf-phone')}
              name="phone"
              countryCode={formData.countryCode}
              phone={formData.phone}
              onCountryCodeChange={(value) => handleInputChange('countryCode', value)}
              onPhoneChange={(value) => handleInputChange('phone', value)}
              setValidity={handleValidityChange}
            />
            <CustomSelect
              label={t('cf-interventionType')}
              name="interventionType"
              value={formData.interventionType}
              onChange={(value) => handleInputChange('interventionType', value)}
              setValidity={handleValidityChange}
              options={getTypeOfInterventions(t)}
            />
            <div className="flex items-center gap-2">
              <CustomTextInput
                label={t('cf-B1Number')}
                name="B1"
                autoComplete=" "
                value={formData.B1}
                onChange={(value) => handleInputChange('B1', value)}
                setValidity={handleValidityChange}
                isNumber={true}
              />
              <TooltipWithHelperIcon title={t('B1-tooltip-title')} body={t('B1-tooltip-desc')} />
            </div>
            <CustomTextarea
              label={t('cf-needs')}
              name="message"
              value={formData.message}
              onChange={(value) => handleInputChange('message', value)}
              setValidity={handleValidityChange}
            />
            <CustomFileUpload text={t('upload-file')} files={formData.files} onChange={(value) => handleInputChange('files', value)} />
          </>
        );
        case 'apiAccess':
        return (
          <>
           <CustomTextInput
                label={t('cf-company')}
                name="companyName"
                autoComplete=" "
                value={formData.companyName}
                onChange={(value) => handleInputChange('companyName', value)}
                setValidity={handleValidityChange}
                isNumber={false}
              />
            <CustomTextarea
              label={t('cf-needs')}
              name="message"
              value={formData.message}
              onChange={(value) => handleInputChange('message', value)}
              setValidity={handleValidityChange}
            />
            <CustomCheckBox name='acceptTerms' labelBeforeCliquableLink={t('api-accept-terms-of-use-label-before-link')} labelCliquableLink={t('api-accept-terms-of-use-label-link')} 
            routeCliquableLink={t('api-accept-terms-of-use-route-link')} labelAfterCliquableLink={t('api-accept-terms-of-use-label-after-link')} setValidity={handleValidityChange} />          </>
        );
        case 'newOrganizationRequest':
          return (
            <>
              <CustomPhoneInput
                name="phone"
                label={t('cf-phone')}
                countryCode={formData.countryCode}
                phone={formData.phone}
                onCountryCodeChange={(value) => handleInputChange('countryCode', value)}
                onPhoneChange={(value) => handleInputChange('phone', value)}
                setValidity={handleValidityChange}
              />
              
              <div/>
              <div className="text-blue-gray-600 dark:text-white font-bold">{t('cf-new-organization-form-organization-info')}</div>
  
              <CustomMandatorySwitch 
                onChange={(value) => {
                  handleInputChange('newOrgaType', value);
                  // If organization type is "Organisation sans BCE", reset BCE field and its validity
                  if (value === ORGANIZATION_TYPES.NO_BCE_ORGANIZATION) {                    
                    handleInputChange('newOrgaBCE', '');
                    handleValidityChange('newOrgaBCE', true);
                  } else {
                    handleValidityChange('newOrgaBCE', false);
                  }
                }} 
                options={orgaTypes} 
                setValidity={(isValid) => handleValidityChange('newOrgaType', isValid)} 
              />
  
              <div className="text-blue-gray-600 dark:text-white font">
              {(() => {
                switch (formData.newOrgaType) {
                  case ORGANIZATION_TYPES.NO_BCE_ORGANIZATION:
                    return t('cf-new-organization-form-organization-description-type-no-bce');
                  case ORGANIZATION_TYPES.EVENT_MANAGER:
                    return t('cf-new-organization-form-organization-description-type-event-manager');
                  case ORGANIZATION_TYPES.EXPERT_OFFICE:
                    return t('cf-new-organization-form-organization-description-type-expert');
                  case ORGANIZATION_TYPES.INSTITUTIONAL_APPLICANT:
                    return t('cf-new-organization-form-organization-description-type-institutional');
                  default:
                    return null;
                }
              })()}
              </div>

              <CustomTextInput
                label={t('cf-new-organization-form-organization-name')}
                name="newOrgaName"
                value={formData.newOrgaName}
                onChange={(value) => handleInputChange('newOrgaName', value)}
                setValidity={handleValidityChange}
                isNumber={false}
              />
  
              {formData.newOrgaType !== ORGANIZATION_TYPES.NO_BCE_ORGANIZATION && (
                <CustomTextInput
                  label={t('cf-new-organization-form-organization-bce')}
                  name="newOrgaBCE"
                  value={formData.newOrgaBCE}
                  onChange={(value) => handleInputChange('newOrgaBCE', value)}
                  setValidity={handleValidityChange}
                  isNumber={false}
                />
              )}
    
              <CustomPhoneInput
                name="newOrgaPhone"
                label={t('cf-new-organization-form-organization-phone')}
                countryCode={formData.newOrgaCountryCode}
                phone={formData.newOrgaPhone}
                onCountryCodeChange={(value) => handleInputChange('newOrgaCountryCode', value)}
                onPhoneChange={(value) => handleInputChange('newOrgaPhone', value)}
                setValidity={handleValidityChange}
              />
  
              <CustomAddressInput
                label={t('cf-new-organization-form-organization-address')}
                fieldname="newOrgaAddress"
                onChange={(value) => handleInputChange('newOrgaAddress', value.newOrgaAddress)}
                setValidity={handleValidityChange}
              />
  
              <div className="text-blue-gray-600 dark:text-white">{t('cf-new-organization-form-organization-language')}</div>
              <CustomMandatorySwitch onChange={(value) => handleInputChange('newOrgaLanguage', value)} options={orgaLanguages} setValidity={(isValid) => handleValidityChange('newOrgaLanguage', isValid)} />
  
              <CustomTextarea
                label={t('cf-needs')}
                name="message"
                value={formData.message}
                onChange={(value) => handleInputChange('message', value)}
                setValidity={handleValidityChange}
              />
              <CustomFileUpload text={t('upload-file')} files={formData.files} onChange={(value) => handleInputChange('files', value)} />
  
            </>
          );
      default:
        return null;
    }
  };

  return (

    <div className="flex justify-center items-center px-4 py-4">
      <Card className="w-full max-w-[30rem]">
        <div className="bg-blue-600 dark:bg-blue-950 p-4 rounded-t-lg">
          <div className="text-2xl font-bold text-white dark:text-yellow-800">
            {formType === 'bug' ? t('cf-reportABug') :
              formType === 'inquiry' ? t('cf-HowCanWeHelpYou') :
                formType === 'assistance' ? t('cf-requestAssistance') :
                  formType === 'moveform' ? t('cf-moveForm') :
                    formType === 'apiAccess' ? t('api-requestAccess') :
                      formType === 'newOrganizationRequest' ? t('cf-newOrganizationRequest') :
                        formType === 'trainingRequest' ? t('training-request-access') : t('cf-contactUs')
                       
            }
            
          </div>
        </div>
        <CardBody className="dark:bg-blue-900">

          {!hasSubmitted ? (
            <form encType="multipart/form-data" className="flex flex-col gap-6" onSubmit={handleSubmit}>
              <div className="my-4 flex items-center gap-4">
                <CustomTextInput
                  label={t('cf-firstName')}
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={(value) => handleInputChange('firstName', value)}
                  setValidity={handleValidityChange}
                  isNumber={false}
                />
                <CustomTextInput
                  label={t('cf-lastName')}
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={(value) => handleInputChange('lastName', value)}
                  setValidity={handleValidityChange}
                  isNumber={false}
                />
              </div>
              <CustomEmailInput
                name="email"
                label={t('cf-mail')}
                value={formData.email}
                onChange={(value) => handleInputChange('email', value)}
                setValidity={handleValidityChange}
              />
              {renderConditionalFields()}

              <Button size="md" type="submit" disabled={!isFormValid}>{t('cf-send')}</Button>

            </form>) : (

            <Message type={messageType} message={message} />

          )}
        </CardBody>
      </Card>
    </div>
  );
}


